import { useState } from 'react';

const Faculty = () => {
  const [selectedDepartment, setSelectedDepartment] = useState('all');

  const facultyMembers = [
    {
      id: 1,
      name: 'Elena Marchetti',
      title: 'Artistic Director & Principal Instructor',
      department: 'leadership',
      image: '/api/placeholder/300/400',
      bio: 'Elena brings over 20 years of professional ballet experience to Northwest Ballet Theater. A former principal dancer with the Royal Ballet, she has performed leading roles in Swan Lake, Giselle, and The Nutcracker. Her passion for teaching and nurturing young talent has made her a beloved figure in the Pacific Northwest ballet community.',
      credentials: [
        'Former Principal Dancer, Royal Ballet',
        'Graduate, Royal Ballet School',
        'Certified Cecchetti Method Instructor',
        'Master of Fine Arts, Dance Pedagogy'
      ],
      specialties: ['Classical Ballet', 'Pointe Technique', 'Performance Coaching']
    },
    {
      id: 2,
      name: 'Marcus Thompson',
      title: 'Ballet Master & Men\'s Technique Instructor',
      department: 'faculty',
      image: '/api/placeholder/300/400',
      bio: 'Marcus is a distinguished ballet master with extensive experience in both performance and pedagogy. His dynamic teaching style and emphasis on strong technique have helped countless male dancers achieve their goals.',
      credentials: [
        'Former Soloist, American Ballet Theatre',
        'Graduate, School of American Ballet',
        'Certified RAD Instructor',
        'Bachelor of Fine Arts, Dance'
      ],
      specialties: ['Men\'s Technique', 'Partnering', 'Character Dance']
    },
    {
      id: 3,
      name: 'Isabella Rodriguez',
      title: 'Pre-Professional Division Director',
      department: 'faculty',
      image: '/api/placeholder/300/400',
      bio: 'Isabella specializes in preparing advanced students for professional careers in ballet. Her comprehensive approach to training encompasses technique, artistry, and the mental preparation needed for a dance career.',
      credentials: [
        'Former Principal, San Francisco Ballet',
        'Graduate, Paris Opera Ballet School',
        'Certified Vaganova Method',
        'Master of Arts, Dance Education'
      ],
      specialties: ['Advanced Ballet', 'Variations', 'Audition Preparation']
    },
    {
      id: 4,
      name: 'David Kim',
      title: 'Contemporary & Modern Dance Instructor',
      department: 'faculty',
      image: '/api/placeholder/300/400',
      bio: 'David brings a fresh perspective to ballet training through his expertise in contemporary and modern dance. His classes help students develop versatility and artistic expression.',
      credentials: [
        'Former Company Member, Martha Graham Dance Company',
        'Graduate, Juilliard School',
        'Certified Horton Technique',
        'Master of Fine Arts, Contemporary Dance'
      ],
      specialties: ['Contemporary Dance', 'Modern Technique', 'Improvisation']
    },
    {
      id: 5,
      name: 'Sophie Laurent',
      title: 'Children\'s Division Director',
      department: 'faculty',
      image: '/api/placeholder/300/400',
      bio: 'Sophie has dedicated her career to early childhood dance education. Her nurturing approach and creative teaching methods make ballet accessible and enjoyable for young dancers.',
      credentials: [
        'Certified Dance Masters of America',
        'Early Childhood Dance Specialist',
        'Bachelor of Arts, Elementary Education',
        'Certified Creative Movement Instructor'
      ],
      specialties: ['Pre-Ballet', 'Creative Movement', 'Early Childhood Development']
    },
    {
      id: 6,
      name: 'James Mitchell',
      title: 'Jazz & Musical Theater Instructor',
      department: 'faculty',
      image: '/api/placeholder/300/400',
      bio: 'James brings Broadway experience to NBT, teaching students the performance skills needed for musical theater and commercial dance.',
      credentials: [
        'Broadway Performer (Chicago, Cats)',
        'Graduate, American Musical and Dramatic Academy',
        'Certified Jazz Dance Instructor',
        'Bachelor of Fine Arts, Musical Theater'
      ],
      specialties: ['Jazz Dance', 'Musical Theater', 'Performance Skills']
    },
    {
      id: 7,
      name: 'Dr. Sarah Chen',
      title: 'Dance Medicine Specialist',
      department: 'staff',
      image: '/api/placeholder/300/400',
      bio: 'Dr. Chen ensures the health and wellness of our dancers through injury prevention, treatment, and education about proper dance conditioning.',
      credentials: [
        'Doctor of Physical Therapy',
        'Certified Dance Medicine Specialist',
        'Sports Medicine Certification',
        'Master of Science, Exercise Physiology'
      ],
      specialties: ['Injury Prevention', 'Rehabilitation', 'Conditioning']
    },
    {
      id: 8,
      name: 'Michael Roberts',
      title: 'Administrative Director',
      department: 'staff',
      image: '/api/placeholder/300/400',
      bio: 'Michael oversees the day-to-day operations of Northwest Ballet Theater, ensuring smooth administration and excellent customer service for all families.',
      credentials: [
        'Master of Business Administration',
        'Bachelor of Arts, Arts Administration',
        '15+ years in nonprofit management',
        'Certified Nonprofit Professional'
      ],
      specialties: ['Operations Management', 'Student Services', 'Program Development']
    }
  ];

  const departments = [
    { id: 'all', label: 'All Faculty & Staff' },
    { id: 'leadership', label: 'Leadership' },
    { id: 'faculty', label: 'Faculty' },
    { id: 'staff', label: 'Staff' }
  ];

  const filteredMembers = selectedDepartment === 'all' 
    ? facultyMembers 
    : facultyMembers.filter(member => member.department === selectedDepartment);

  return (
    <main className="min-h-screen bg-ballet-light">
      {/* Hero Section */}
      <div className="bg-elegant-gradient text-white py-20">
        <div className="container-custom text-center">
          <h1 className="text-5xl lg:text-6xl font-elegant font-bold mb-6">
            Faculty & Staff
          </h1>
          <p className="text-xl lg:text-2xl text-white/90 max-w-3xl mx-auto">
            Meet our dedicated team of professional instructors and staff members
          </p>
        </div>
      </div>

      <div className="container-custom section-padding">
        {/* Department Filter */}
        <div className="flex flex-wrap justify-center mb-12 bg-white rounded-2xl p-2 shadow-elegant">
          {departments.map((department) => (
            <button
              key={department.id}
              onClick={() => setSelectedDepartment(department.id)}
              className={`px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
                selectedDepartment === department.id
                  ? 'bg-ballet-burgundy text-white shadow-lg'
                  : 'text-ballet-burgundy hover:bg-ballet-light'
              }`}
            >
              {department.label}
            </button>
          ))}
        </div>

        {/* Faculty Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {filteredMembers.map((member) => (
            <div key={member.id} className="card-elevated group hover:shadow-2xl transition-all duration-300">
              <div className="overflow-hidden rounded-t-2xl">
                <img
                  src={member.image}
                  alt={member.name}
                  className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                />
              </div>
              <div className="p-6">
                <h3 className="text-xl font-elegant font-bold text-ballet-burgundy mb-2">
                  {member.name}
                </h3>
                <p className="text-gray-600 font-semibold mb-4">{member.title}</p>
                
                <p className="text-gray-700 text-sm mb-4 line-clamp-3">
                  {member.bio}
                </p>
                
                {/* Specialties */}
                <div className="mb-4">
                  <h4 className="text-sm font-semibold text-ballet-burgundy mb-2">Specialties:</h4>
                  <div className="flex flex-wrap gap-2">
                    {member.specialties.map((specialty, index) => (
                      <span
                        key={index}
                        className="bg-ballet-light text-ballet-burgundy px-2 py-1 rounded-full text-xs font-medium"
                      >
                        {specialty}
                      </span>
                    ))}
                  </div>
                </div>
                
                <button className="w-full btn-outline text-sm">
                  View Full Bio
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Join Our Team Section */}
        <div className="card-elevated">
          <div className="p-8 lg:p-12 text-center">
            <h2 className="text-3xl font-elegant font-bold gradient-text mb-6">
              Join Our Team
            </h2>
            <p className="text-lg text-gray-700 max-w-3xl mx-auto mb-8">
              Northwest Ballet Theater is always looking for qualified, passionate instructors 
              to join our faculty. If you have professional dance experience and a love for 
              teaching, we'd love to hear from you.
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="bg-ballet-light p-6 rounded-xl">
                <div className="w-12 h-12 bg-ballet-burgundy rounded-xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <h3 className="font-semibold text-ballet-burgundy mb-2">Professional Experience</h3>
                <p className="text-gray-700 text-sm">We seek instructors with professional dance backgrounds and teaching experience.</p>
              </div>
              
              <div className="bg-ballet-light p-6 rounded-xl">
                <div className="w-12 h-12 bg-ballet-burgundy rounded-xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-ballet-burgundy mb-2">Passion for Teaching</h3>
                <p className="text-gray-700 text-sm">A genuine love for working with students and helping them achieve their goals.</p>
              </div>
              
              <div className="bg-ballet-light p-6 rounded-xl">
                <div className="w-12 h-12 bg-ballet-burgundy rounded-xl flex items-center justify-center mx-auto mb-4">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="font-semibold text-ballet-burgundy mb-2">Team Collaboration</h3>
                <p className="text-gray-700 text-sm">Ability to work collaboratively with our existing faculty and staff team.</p>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="btn-primary">
                Submit Application
              </button>
              <button className="btn-outline">
                View Open Positions
              </button>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
};

export default Faculty;
